import React from 'react';
import { DateSelectArg, EventClickArg, EventInput } from '@fullcalendar/core';
import { Box, Menu, MenuItem } from '@mui/material';
import { FasitCalendarWrapper } from '../../../components/calendar';
import { CalendarEventType } from '../../../api-service/clients';
import { WindowService } from '../../../components/window-service/window-service';
import { useServiceProviderCalendarBusinessHours } from '../../citizen/serviceprovider/calendar/hooks/use-service-provider-calendar-business-hours';
import { usePendingReschedule } from '../../citizen/serviceprovider/calendar/hooks/use-pending-reschedule';
import {
  CreateEventDialog,
  CreateEventDialogSubmitValues,
} from '../../citizen/serviceprovider/calendar/dialogs/create-event-dialog';
import { EventDialog } from '../../citizen/serviceprovider/calendar/dialogs/event-dialog';
import type { ManageServiceProviderEventDialogEvent, UpdateServiceProviderEventValues } from '../../citizen/serviceprovider/calendar/dialogs/event-dialog-types';
import { ConfirmRescheduleDialog } from '../../citizen/serviceprovider/calendar/dialogs/confirm-reschedule-dialog';
import { SeriesOccurrenceScopeChooserDialog } from '../../citizen/serviceprovider/calendar/dialogs/series-occurrence-scope-chooser-dialog';
import { mapCalendarEventClickToManageDialogEvent } from '../../citizen/serviceprovider/calendar/calendar-event-interaction-mappers';
import { renderServiceProviderCalendarEventContent } from './calendar-event-content';
import type { RecurrencePattern } from '../../../components/calendar/recurrence/recurrence-pattern';

interface CreateEventInput extends CreateEventDialogSubmitValues {
  serviceProviderUnitId: string;
}

interface UpdateEventInput {
  eventId: string;
  title: string;
  locationAddress: string;
  startAt: Date | string;
  endAt: Date | string;
  allDay: boolean;
  content?: string;
  responsibleUserId: string;
  consumedMinutes?: number;
  recurrence?: RecurrencePattern;
  noteTypeId?: string;
}

/**
 * Callbacks for per-occurrence actions on recurring series events.
 * The host (citizen calendar or SPU calendar) injects the correct client implementation.
 * Each callback maps to one BFF per-occurrence command endpoint.
 */
export interface SeriesOccurrenceCallbacks {
  updateOccurrence: (values: UpdateServiceProviderEventValues, occurrenceStartAt: Date) => Promise<void>;
  markOccurrenceDone: (seriesId: string, occurrenceStartAt: Date, comment?: string, consumedMinutes?: number) => Promise<void>;
  cancelOccurrence: (seriesId: string, occurrenceStartAt: Date, comment?: string, consumedMinutes?: number) => Promise<void>;
  deleteOccurrence: (seriesId: string, occurrenceStartAt: Date) => Promise<void>;
}

export interface ServiceProviderCalendarShellProps {
  events: EventInput[];
  isLoading: boolean;
  serviceProviderUnitId: string | undefined;
  currentUserId: string | undefined;
  printTitle: string;
  heightOffsetPx?: number;
  enrichSelectedEvent?: (
    event: ManageServiceProviderEventDialogEvent
  ) => ManageServiceProviderEventDialogEvent;
  createEvent: (data: CreateEventInput) => Promise<void>;
  updateEvent: (data: UpdateEventInput) => Promise<void>;
  markDone: (
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ) => Promise<void>;
  cancelEvent: (
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ) => Promise<void>;
  unmarkEvent: (eventId: string) => Promise<void>;
  deleteEvent: (eventId: string) => Promise<void>;
  /** Per-occurrence command callbacks for recurring series events. */
  seriesOccurrenceCallbacks?: SeriesOccurrenceCallbacks;
  onVisibleRangeChange: (range: { start: Date; end: Date }) => void;
  onFollowupCreated?: () => void;
  onObservationCreated?: () => void;
}

const DEFAULT_HEIGHT_OFFSET_PX = 240;
const DEFAULT_EVENT_DURATION_MS = 60 * 60 * 1000;

export function ServiceProviderCalendarShell(
  props: ServiceProviderCalendarShellProps
) {
  const {
    events,
    isLoading,
    serviceProviderUnitId,
    currentUserId,
    printTitle,
    heightOffsetPx = DEFAULT_HEIGHT_OFFSET_PX,
    enrichSelectedEvent,
    createEvent,
    updateEvent,
    markDone,
    cancelEvent,
    unmarkEvent,
    deleteEvent,
    seriesOccurrenceCallbacks,
    onVisibleRangeChange,
    onFollowupCreated,
    onObservationCreated,
  } = props;

  const businessHours = useServiceProviderCalendarBusinessHours();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [pendingCreateDialogRange, setPendingCreateDialogRange] =
    React.useState<{ startAt: Date; endAt: Date } | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = React.useState<{
    left: number;
    top: number;
  } | null>(null);
  const [createDialogStartAt, setCreateDialogStartAt] = React.useState(
    new Date()
  );
  const [createDialogEndAt, setCreateDialogEndAt] = React.useState(
    new Date(Date.now() + DEFAULT_EVENT_DURATION_MS)
  );
  const [selectedEvent, setSelectedEvent] =
    React.useState<ManageServiceProviderEventDialogEvent | null>(null);
  // Pending recurring occurrence: held while the scope chooser is visible.
  const [pendingOccurrenceEvent, setPendingOccurrenceEvent] =
    React.useState<ManageServiceProviderEventDialogEvent | null>(null);
  // Scope chosen for a series drag/resize; once set, the reschedule confirmation is shown
  // before applying. Null means the scope chooser is still the active step.
  const [rescheduleScope, setRescheduleScope] =
    React.useState<'occurrence' | 'series' | null>(null);

  const {
    pendingReschedule,
    rescheduleError,
    isRescheduleSubmitting,
    handleEventDrop,
    handleEventResize,
    handleCancelReschedule,
    handleConfirmReschedule,
    applyToOccurrence,
    applyToSeries,
  } = usePendingReschedule({
    updateEvent,
    updateOccurrence: seriesOccurrenceCallbacks?.updateOccurrence,
  });

  function openCreateDialog(initialStartAt?: Date, initialEndAt?: Date) {
    const startAt = initialStartAt ?? new Date();
    const endAt =
      initialEndAt ?? new Date(startAt.getTime() + DEFAULT_EVENT_DURATION_MS);

    setCreateDialogStartAt(startAt);
    setCreateDialogEndAt(endAt);
    setIsCreateDialogOpen(true);
  }

  function closeCreateDialog() {
    setIsCreateDialogOpen(false);
    setPendingCreateDialogRange(null);
  }

  function closeManageDialog() {
    setSelectedEvent(null);
  }

  function closeScopeChooser() {
    setPendingOccurrenceEvent(null);
  }

  // "Hele serien" chosen — open manage dialog bound to the master anchor.
  function handleChooseSeries() {
    if (!pendingOccurrenceEvent) {
      return;
    }
    // Strip occurrence-specific fields; the master anchor (startAt/endAt) is already in the event.
    const masterEvent: ManageServiceProviderEventDialogEvent = {
      ...pendingOccurrenceEvent,
      isSeriesOccurrence: false,
      occurrenceStartAt: undefined,
      occurrenceEndAt: undefined,
    };
    setPendingOccurrenceEvent(null);
    setSelectedEvent(masterEvent);
  }

  // "Denne begivenhed" chosen — open manage dialog bound to the occurrence.
  function handleChooseOccurrence() {
    if (!pendingOccurrenceEvent) {
      return;
    }
    setPendingOccurrenceEvent(null);
    setSelectedEvent(pendingOccurrenceEvent);
  }

  async function handleEventClick(eventClickInfo: EventClickArg) {
    if (eventClickInfo.event.extendedProps?.type === CalendarEventType.Event) {
      eventClickInfo.jsEvent.preventDefault();
      const mappedEvent =
        mapCalendarEventClickToManageDialogEvent(eventClickInfo);
      if (!mappedEvent) {
        return;
      }

      const enrichedEvent = enrichSelectedEvent
        ? enrichSelectedEvent(mappedEvent)
        : mappedEvent;

      // When the clicked event is a recurring occurrence and occurrence callbacks are
      // available, show the scope chooser first.
      if (enrichedEvent.isSeriesOccurrence && seriesOccurrenceCallbacks) {
        setPendingOccurrenceEvent(enrichedEvent);
        return;
      }

      setSelectedEvent(enrichedEvent);
      return;
    }

    const eventUrl = eventClickInfo.event.url;
    if (!eventUrl) {
      return;
    }

    eventClickInfo.jsEvent.preventDefault();
    WindowService.open(eventUrl, eventUrl);
  }

  function handleCalendarSelect(selectInfo: DateSelectArg) {
    setPendingCreateDialogRange({
      startAt: selectInfo.start,
      endAt: selectInfo.end,
    });
  }

  function handleAddEventClick() {
    if (pendingCreateDialogRange) {
      openCreateDialog(
        pendingCreateDialogRange.startAt,
        pendingCreateDialogRange.endAt
      );
      return;
    }

    openCreateDialog();
  }

  function handleCalendarContextMenu(event: React.MouseEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.fc-view-harness')) {
      return;
    }

    event.preventDefault();
    setContextMenuPosition({ left: event.clientX, top: event.clientY });
  }

  function closeContextMenu() {
    setContextMenuPosition(null);
  }

  function handleContextMenuCreateClick() {
    closeContextMenu();
    handleAddEventClick();
  }

  async function handleCreateEventSubmit(
    values: CreateEventDialogSubmitValues
  ) {
    if (!serviceProviderUnitId) {
      return;
    }

    await createEvent({
      serviceProviderUnitId,
      ...values,
    });
    setIsCreateDialogOpen(false);
  }

  return (
    <>
      {isCreateDialogOpen && (
        <CreateEventDialog
          key={`${createDialogStartAt.toISOString()}-${createDialogEndAt.toISOString()}-${
            currentUserId ?? ''
          }`}
          onClose={closeCreateDialog}
          initialStartAt={createDialogStartAt}
          initialEndAt={createDialogEndAt}
          initialResponsibleUserId={currentUserId}
          onSubmit={handleCreateEventSubmit}
        />
      )}
      {pendingOccurrenceEvent && (
        <SeriesOccurrenceScopeChooserDialog
          key={`${pendingOccurrenceEvent.eventId}-${pendingOccurrenceEvent.occurrenceStartAt?.toISOString() ?? ''}`}
          onChooseOccurrence={handleChooseOccurrence}
          onChooseSeries={handleChooseSeries}
          onClose={closeScopeChooser}
        />
      )}
      {selectedEvent && (
        <EventDialog
          key={`${selectedEvent.eventId}-${selectedEvent.isSeriesOccurrence ? selectedEvent.occurrenceStartAt?.toISOString() ?? '' : 'master'}`}
          event={selectedEvent}
          onClose={closeManageDialog}
          onUpdate={updateEvent}
          onMarkDone={markDone}
          onCancel={cancelEvent}
          onUnmark={unmarkEvent}
          onDelete={deleteEvent}
          occurrenceCallbacks={
            seriesOccurrenceCallbacks
              ? {
                  onUpdateOccurrence: seriesOccurrenceCallbacks.updateOccurrence,
                  onMarkOccurrenceDone: seriesOccurrenceCallbacks.markOccurrenceDone,
                  onCancelOccurrence: seriesOccurrenceCallbacks.cancelOccurrence,
                  onDeleteOccurrence: seriesOccurrenceCallbacks.deleteOccurrence,
                }
              : undefined
          }
          onFollowupCreated={() => {
            closeManageDialog();
            onFollowupCreated?.();
          }}
          currentUserId={currentUserId}
          onObservationCreated={() => {
            closeManageDialog();
            onObservationCreated?.();
          }}
        />
      )}
      {pendingReschedule && pendingReschedule.isSeriesOccurrence && rescheduleScope === null && (
        <SeriesOccurrenceScopeChooserDialog
          key={`reschedule-${pendingReschedule.seriesId ?? pendingReschedule.eventId}-${pendingReschedule.occurrenceStartAt?.toISOString() ?? ''}-${pendingReschedule.actionType}`}
          onChooseOccurrence={() => setRescheduleScope('occurrence')}
          onChooseSeries={() => setRescheduleScope('series')}
          onClose={() => {
            setRescheduleScope(null);
            handleCancelReschedule();
          }}
        />
      )}
      {pendingReschedule && pendingReschedule.isSeriesOccurrence && rescheduleScope !== null && (
        <ConfirmRescheduleDialog
          key={`reschedule-confirm-${pendingReschedule.seriesId ?? pendingReschedule.eventId}-${pendingReschedule.actionType}`}
          pendingReschedule={pendingReschedule}
          error={rescheduleError}
          isSubmitting={isRescheduleSubmitting}
          onCancel={() => {
            setRescheduleScope(null);
            handleCancelReschedule();
          }}
          onConfirm={async () => {
            const scope = rescheduleScope;
            if (scope === 'occurrence') {
              await applyToOccurrence();
            } else {
              await applyToSeries();
            }
            setRescheduleScope(null);
          }}
        />
      )}
      {pendingReschedule && !pendingReschedule.isSeriesOccurrence && (
        <ConfirmRescheduleDialog
          key={`${pendingReschedule.eventId}-${pendingReschedule.actionType}`}
          pendingReschedule={pendingReschedule}
          error={rescheduleError}
          isSubmitting={isRescheduleSubmitting}
          onCancel={handleCancelReschedule}
          onConfirm={handleConfirmReschedule}
        />
      )}
      <Box onContextMenu={handleCalendarContextMenu}>
        <FasitCalendarWrapper
          toolbarVariant="v2"
          onVisibleRangeChange={onVisibleRangeChange}
          onAddEventClick={handleAddEventClick}
          isLoading={isLoading}
          events={events}
          eventContent={renderServiceProviderCalendarEventContent}
          businessHours={businessHours}
          weekends
          printTitle={printTitle}
          eventClick={handleEventClick}
          editable
          eventDurationEditable
          selectable
          select={handleCalendarSelect}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          height={`calc(100vh - ${heightOffsetPx}px)`}
          scrollTime="06:00:00"
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            omitZeroMinute: false,
          }}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            omitZeroMinute: false,
          }}
        />
      </Box>
      <Menu
        open={Boolean(contextMenuPosition)}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuPosition
            ? { top: contextMenuPosition.top, left: contextMenuPosition.left }
            : undefined
        }
      >
        <MenuItem onClick={handleContextMenuCreateClick}>Opret</MenuItem>
      </Menu>
    </>
  );
}
